import { startTransition, useCallback, useEffect, useRef, useState } from 'react';
import { isFullScreenLoadingIndicatorVisible } from '../../components/FullScreenLoadingIndicator';
import { supportsViewTransitions } from './utils';
import {
  runAfterTransitionCallbacks,
  runBeforeTransitionCallbacks,
} from './viewTransitionRegistry';

export const useViewTransitionRunner = (initialPathname: string) => {
  const [finishViewTransition, setFinishViewTransition] = useState<null | (() => void)>(null);
  const previousPathnameRef = useRef(initialPathname);

  const runViewTransitions = useCallback(
    async ({ nextPathname, navigate }: { nextPathname: string; navigate: () => void }) => {
      if (!supportsViewTransitions() || isFullScreenLoadingIndicatorVisible.current) {
        // If View Transitions API is not supported or the full screen loading indicator is visible,
        // fallback to direct navigation.
        navigate();
        return;
      }
      const currentPathname = previousPathnameRef.current;
      previousPathnameRef.current = nextPathname;

      // Run the before transition callbacks before the view transition starts.
      await runBeforeTransitionCallbacks({ currentPathname, nextPathname });

      const transition = document.startViewTransition(
        () =>
          new Promise<void>((resolve) => {
            startTransition(() => {
              navigate();
              previousPathnameRef.current = nextPathname;
              setFinishViewTransition(() => resolve);
            });
          })
      );

      // Run the after transition callbacks when the view transition is finished.
      transition.finished.then(() => {
        runAfterTransitionCallbacks();
      });
    },
    []
  );

  // If a finishViewTransition function exists, call it to finish the view transition.
  useEffect(() => {
    if (finishViewTransition) {
      finishViewTransition();
      setFinishViewTransition(null);
    }
  }, [finishViewTransition]);

  return runViewTransitions;
};
